import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { UploadReportValidation, useGenerateIngestReportUpload } from "@/lib/api/exportSalesTracking";
import { useGetCreateJobStatus } from "@/lib/api/dorman";
import { MimeType } from "@/lib/api/utils";
import { UploadPresignedFile } from "@/lib/file";
import { hasAccessToFeature, PermissionType, PossiblePackage } from "@/lib/models/auth";
import { useStytchMember } from "@stytch/react/dist/b2b";
import { UploadIcon } from "lucide-react";
import { useRef, useEffect, useState } from "react";


export function UploadIngestReportDialog() {
  const { toast } = useToast();
  const [link, setLink] = useState<string | undefined | null>();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const jobStatusRes = useGetCreateJobStatus(jobId);
  const createUploadJob = useGenerateIngestReportUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onGenerateReport = async (e: React.FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    try {
      if (!fileToUpload) { return; }
      if (isGenerating) { return; }
      setIsGenerating(true);

      const parseDataResult = UploadReportValidation.safeParse({
        filename: fileToUpload?.name || "",
        mime_type: fileToUpload?.type as any || ""
      });

      if (!parseDataResult.success) {
        toast({
          title: "Invalid File"
        })
        return;
      }

      const result = await createUploadJob(parseDataResult.data);
      if (!result.success) {
        throw result;
      }

      const resultData = result.data;
      setJobId(resultData.job_id);

      const uploadResult = await UploadPresignedFile(resultData.link, fileToUpload);
      if (!result.success) {
        console.error("Failed to upload report", uploadResult);
        toast({
          title: "Failed to upload report"
        })
        return;
      }

      toast({
        title: "Processing now.",
        description: "",
      });
      setIsOpen(false);
    } catch (error) {
      setLink(null);
      console.error("Failed to generate crop profile report", error);

      toast({
        title: "Failed to generate report",
        description: "",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const hasFetchedJob = !!jobStatusRes?.data || false;
  const jobStatus = jobStatusRes?.data?.data?.job_status;


  return (
    <Dialog
      open={isOpen}
      onOpenChange={(status) => {
        if (!status) { setLink(null); }
        if (status) {
          setJobId(undefined);
        }
        setIsOpen(status);
      }}
    >
      <DialogTrigger>
        <span className="sr-only">Upload Export Sales Report</span>
        <Button className="gap-2" asChild>
          <span>
            {!jobId && (
              <>
                <UploadIcon className="h-4 w-4" />
                Upload Report
            </>
            )}
            {jobId && <JobStatus jobStatus={jobStatus} setJobId={setJobId} /> }
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Upload Sales Details
          </DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={onGenerateReport}>
          <span className="grid gap-2">
            <Label htmlFor="cashSalesFileInput" className="font-semibold">Report</Label>
            <Input
              id="salesDetailsFileInput"
              type="file"
              ref={fileInputRef}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFileToUpload(event.target.files?.[0] || null)}
              accept={MimeType.Xlsx}
            />
          </span>

          <Button type="submit" disabled={isGenerating}>Upload</Button>
        </form>
        {isGenerating && (<p>Uploading Report</p>)}
      </DialogContent>
    </Dialog>
  );
}

function JobStatus({ jobStatus, setJobId }: { jobStatus: string; setJobId: any }) {
    const className = "text-xs font-bold tracking-tight";

  useEffect(() => {
    if (jobStatus === "completed") {
      setJobId(undefined);
    }
  }, [jobStatus])

    if (!jobStatus) { return null }
    if (jobStatus === "queued") { return (<div className={className}>Processing</div>) }
    if (jobStatus === "doesnt_exist") { return (<div className={className}>Failed</div>) }
    if (jobStatus === "failed") { return (<div className={className}>Failed</div>) }
    if (jobStatus === "halt") { return (<div className={className}>Failed</div>) }
    if (jobStatus !== "completed") { return (<div className={className}>Unknown</div>); }

    return null;
}
